// ChallanStyles.js — All styles for ChallanDetails component
// Improved: consistent tag sizing, full responsiveness, visual hierarchy

import { systemColor, tagBrownBgColor, tagBrownTextColor, white } from "../../../config/ColorObj";
import {
    // Status — closed
    closedBgColor,
    closedTextColor,
    closedDotColor,

    // Status — active
    activeBgColor,
    activeTextColor,
    activeDotColor,

    // Status — overdue
    overdueBgColor,
    overdueTextColor,
    overdueDotColor,

    // Text hierarchy
    textPrimaryColor,
    textSecondaryColor,
    textMutedColor,
    textMutedSubtitleColor,

    // Surfaces
    pageBgColor,
    surfaceAltColor,
    borderColor,
    borderLightColor,

    // Metric text
    metricGreenColor,
    metricAmberColor,
    metricRedColor,
    metricBlueColor,

    // Bar fills
    barGreenColor,
    barAmberColor,
    barRedColor,

    // Tags — green
    tagGreenBgColor,
    tagGreenTextColor,

    // Tags — amber
    tagAmberBgColor,
    tagAmberTextColor,

    // Tags — red
    tagRedBgColor,
    tagRedTextColor,

    // Tags — blue
    tagBlueBgColor,
    tagBlueTextColor,

    // Tags — purple
    tagPurpleBgColor,
    tagPurpleTextColor,

    // Tags — gray
    tagGrayBgColor,
    tagGrayTextColor,

    // Alert — danger
    dangerBgColor,
    dangerBorderColor,
    dangerTextColor,
    dangerDotColor,

    // Alert — warning
    warnBgColor,
    warnBorderColor,
    warnTextColor,
    warnDotColor,

    // Accent
    accentColor,
    accentLightColor,

    // Timeline & misc
    timelinePendingBgColor,
    timelinePendingBorderColor,
    timelineClosedBgColor,
    timelineLineNeutralColor,
    metricBarBgColor,

    // Details / icon circle
    iconCircleColor,

    // Challan card
    challanOpenBorderColor,

    // Insights
    insightsDotColor,
} from "../../../config/ColorObj";

export const COLORS = {
    // Status
    closedBg: closedBgColor,
    closedText: closedTextColor,
    closedDot: closedDotColor,
    activeBg: activeBgColor,
    activeText: activeTextColor,
    activeDot: activeDotColor,
    overdueBg: overdueBgColor,
    overdueText: overdueTextColor,
    overdueDot: overdueDotColor,

    // Text hierarchy
    textPrimary: textPrimaryColor,
    textSecondary: textSecondaryColor,
    textMuted: textMutedColor,
    textMutedForSubTite: textMutedSubtitleColor,
    textWhite: white,

    // Surface
    bg: pageBgColor,
    cardBg: white,
    surfaceAlt: surfaceAltColor,
    border: borderColor,
    borderLight: borderLightColor,

    // Metrics
    green: metricGreenColor,
    amber: metricAmberColor,
    red: metricRedColor,
    blue: metricBlueColor,
    neutral: textPrimaryColor,

    // Bars
    barGreen: barGreenColor,
    barAmber: barAmberColor,
    barRed: barRedColor,

    // Tag backgrounds
    tagGreenBg: tagGreenBgColor,
    tagGreenText: tagGreenTextColor,
    tagAmberBg: tagAmberBgColor,
    tagAmberText: tagAmberTextColor,
    tagRedBg: tagRedBgColor,
    tagRedText: tagRedTextColor,
    tagBlueBg: tagBlueBgColor,
    tagBlueText: tagBlueTextColor,
    tagPurpleBg: tagPurpleBgColor,
    tagPurpleText: tagPurpleTextColor,
    tagGrayBg: tagGrayBgColor,
    tagGrayText: tagGrayTextColor,
    tagBrownBg: tagBrownBgColor,
    tagBrownText: tagBrownTextColor,

    // Alerts
    dangerBg: dangerBgColor,
    dangerBorder: dangerBorderColor,
    dangerText: dangerTextColor,
    warnBg: warnBgColor,
    warnBorder: warnBorderColor,
    warnText: warnTextColor,

    // Accent
    accent: accentColor,
    accentLight: accentLightColor,
};

export const FONTS = {
    base: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
};

// ─── Responsive breakpoint helpers ────────────────────────────────────────────
// Usage: import and apply via inline style + useMediaQuery or window.innerWidth checks
// For pure inline styles we provide object factories that accept a `bp` string.
// bp: "mobile" | "tablet" | "desktop"  (caller decides based on window.innerWidth)

// Layout
export const layout = {
    page: {
        fontFamily: FONTS.base,
        padding: "0",
        boxSizing: "border-box",
    },
    container: {
        padding: "8px 8px 16px",
        margin: "0 auto",
        boxSizing: "border-box",
    },
};

// Search & Filter Bar
export const filterBar = {
    wrapper: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        marginTop: 15,
    },
    searchWrap: {
        position: "relative",
        flex: "1 1 200px",
        minWidth: 200,
    },
    searchIcon: {
        position: "absolute",
        left: 12,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        color: COLORS.textMuted,
        display: "flex",
        alignItems: "center",
    },
    searchInput: {
        width: "100%",
        padding: "9px 14px 9px 36px",
        border: `1.5px solid ${COLORS.border}`,
        borderRadius: 10,
        fontSize: 13,
        background: COLORS.cardBg,
        color: COLORS.textPrimary,
        outline: "none",
        fontFamily: FONTS.base,
        boxSizing: "border-box",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    select: {
        padding: "9px 14px",
        border: `1.5px solid ${COLORS.border}`,
        borderRadius: 10,
        fontSize: 12,
        background: COLORS.cardBg,
        color: COLORS.textSecondary,
        outline: "none",
        fontFamily: FONTS.base,
        cursor: "pointer",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        fontWeight: 500,
    },
    legend: {
        display: "flex",
        gap: 20,
        alignItems: "center",
        flexWrap: "wrap",
        paddingLeft: 4,
        flexShrink: 0,
        marginTop: -55,
    },
    legendItem: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        color: COLORS.textSecondary,
        fontWeight: 600,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
    },
    legendDot: (color) => ({
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
    }),
};

// Outer parent card (scroll container)
export const parentCard = {
    wrapper: {
        background: COLORS.cardBg,
        border: `1.5px solid ${COLORS.border}`,
        borderRadius: 16,
        boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
        display: "flex",
        flexDirection: "column",
    },
    header: {
        padding: "12px 16px",
        borderBottom: `1.5px solid ${COLORS.borderLight}`,
        background: systemColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "14px 14px 0 0",
        flexShrink: 0,
        gap: 8,
        flexWrap: "wrap",
    },
    headerTitle: {
        fontSize: 15,
        fontWeight: 700,
        color: white,
        letterSpacing: "-0.01em",
    },
    headerCount: {
        fontSize: 11,
        color: COLORS.textMuted,
        fontWeight: 600,
        background: COLORS.borderLight,
        padding: "2px 10px",
        borderRadius: 20,
        whiteSpace: "nowrap",
    },
    scrollBody: {
        height: "calc(100vh - 240px)",
        minHeight: 360,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "12px 12px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        scrollbarWidth: "thin",
        scrollbarColor: `${COLORS.border} transparent`,
    },
    scrollBodyWhenNoChallandFound: {
        height: "calc(20vh - 30px)",
        minHeight: 30,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "12px 12px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        scrollbarWidth: "thin",
        scrollbarColor: `${COLORS.border} transparent`,
    },
};

// ── Tag pill ──────────────────────────────────────────────────────────────────
// FIX: uniform width via minWidth + flex-basis ensures both tags in each row
// are the same visual size regardless of text length.
export const tag = (colorKey) => {
    const map = {
        green: { bg: COLORS.tagGreenBg, color: COLORS.tagGreenText },
        amber: { bg: COLORS.tagAmberBg, color: COLORS.tagAmberText },
        red: { bg: COLORS.tagRedBg, color: COLORS.tagRedText },
        blue: { bg: COLORS.tagBlueBg, color: COLORS.tagBlueText },
        purple: { bg: COLORS.tagPurpleBg, color: COLORS.tagPurpleText },
        brown: { bg: COLORS.tagBrownBg, color: COLORS.tagBrownText },
        gray: { bg: COLORS.tagGrayBg, color: COLORS.tagGrayText },
    };
    const c = map[colorKey] || map.gray;
    return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-start", // align text+icon to left inside fixed pill
        fontSize: 11,
        fontWeight: 600,
        // IMPROVED: use flex: "1 1 0" on the tag itself (set in tagRow's children)
        // and a consistent minWidth so every pill in a row has equal minimum footprint.
        minWidth: 128,
        maxWidth: 180,
        width: "100%", // fill parent flex column equally
        padding: "5px 10px",
        borderRadius: 20,
        background: c.bg,
        color: c.color,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        border: `1px solid ${c.color}22`,
        lineHeight: 1.4,
        boxSizing: "border-box",
        gap: 5,
    };
};

// Status Badge
export const statusBadge = (status) => {
    const map = {
        Closed: { bg: COLORS.activeBg, color: COLORS.activeText },
        Active: { bg: COLORS.closedBg, color: COLORS.closedText },
        Pending: { bg: COLORS.overdueBg, color: COLORS.overdueText },
    };
    const c = map[status] || map.Closed;
    return {
        fontSize: 10,
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 20,
        background: c.bg,
        color: c.color,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        flexShrink: 0,
    };
};

// Flag Strip
export const flagStrip = (isDanger) => ({
    wrapper: {
        background: isDanger ? COLORS.dangerBg : COLORS.warnBg,
        border: `1px solid ${isDanger ? COLORS.dangerBorder : COLORS.warnBorder}`,
        borderRadius: 10,
        padding: "10px 13px",
        display: "flex",
        gap: 9,
        alignItems: "flex-start",
        marginBottom: 16,
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: isDanger ? dangerDotColor : warnDotColor,
        flexShrink: 0,
        marginTop: 4,
    },
    text: {
        fontSize: 12,
        color: isDanger ? COLORS.dangerText : COLORS.warnText,
        lineHeight: 1.6,
    },
});

// Section label
export const sectionLabel = {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 6,
};

// Timeline
export const timeline = {
    container: {
        display: "flex",
        alignItems: "flex-start",
        overflowX: "auto",
        paddingBottom: 8,
        marginBottom: 20,
        gap: 0,
        // FIX: ensure timeline scrolls horizontally on mobile without breaking layout
        WebkitOverflowScrolling: "touch",
    },
    step: {
        flex: 1,
        minWidth: 64,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
    },
    dot: (state) => {
        const base = {
            width: 26,
            height: 26,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            zIndex: 1,
            marginBottom: 8,
        };
        const map = {
            done: { background: closedBgColor, border: `2px solid ${closedDotColor}` },
            active: { background: activeBgColor, border: `2px solid ${activeDotColor}` },
            overdue: { background: overdueBgColor, border: `2px solid ${overdueDotColor}` },
            closed: { background: timelineClosedBgColor, border: `2px solid ${accentColor}` },
            pending: { background: timelinePendingBgColor, border: `2px solid ${timelinePendingBorderColor}` },
        };
        return { ...base, ...(map[state] || map.pending) };
    },
    line: (state) => ({
        position: "absolute",
        top: 13,
        left: "50%",
        width: "100%",
        height: 2,
        background:
            state === "done"
                ? closedDotColor
                : state === "active"
                    ? activeDotColor
                    : state === "overdue"
                        ? overdueDotColor
                        : timelineLineNeutralColor,
        zIndex: 0,
    }),
    stepLabel: (state) => ({
        fontSize: 10,
        fontWeight: 600,
        color: state === "overdue" ? metricRedColor : COLORS.textSecondary,
        textAlign: "center",
        lineHeight: 1.3,
    }),
    stepDate: (state) => ({
        fontSize: 10,
        color: state === "overdue" ? overdueDotColor : COLORS.textMuted,
        textAlign: "center",
        marginTop: 2,
    }),
};

// Metrics grid — responsive: 2 cols on mobile, auto-fill on larger
export const metrics = {
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: 8,
        marginBottom: 16,
        marginTop: 40,
    },
    card: {
        background: COLORS.surfaceAlt,
        borderRadius: 10,
        padding: "11px 13px",
        border: `1px solid ${iconCircleColor}`,
        transition: "border-color 0.15s",
    },
    label: {
        fontSize: 10,
        color: COLORS.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        marginBottom: 4,
    },
    value: (colorKey) => {
        const map = {
            green: COLORS.green,
            amber: COLORS.amber,
            red: COLORS.red,
            blue: COLORS.blue,
            neutral: COLORS.neutral,
        };
        return {
            fontSize: 17,
            fontWeight: 700,
            color: map[colorKey] || COLORS.neutral,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
        };
    },
    bar: {
        wrap: { marginTop: 6, background: metricBarBgColor, borderRadius: 4, height: 4 },
        fill: (pct, colorKey) => {
            const map = {
                green: COLORS.barGreen,
                amber: COLORS.barAmber,
                red: COLORS.barRed,
            };
            return {
                width: `${pct}%`,
                height: 4,
                borderRadius: 4,
                background: map[colorKey] || COLORS.barGreen,
            };
        },
    },
    sub: {
        fontSize: 10,
        color: COLORS.textMuted,
        marginTop: 5,
        lineHeight: 1.4,
    },
};

// Detail cards — single horizontal row, fixed card size, scroll on narrow viewports
const DETAIL_CARD_WIDTH = 400;
const DETAIL_CARD_HEIGHT = 92;

export const details = {
    // Flex row wrapper: keeps all cards on one line; scrolls horizontally when viewport is too narrow
    row: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "stretch",
        gap: 8,
        width: "100%",
        marginBottom: 16,
        marginTop: 40,
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",
        scrollbarColor: `${COLORS.border} transparent`,
    },

    // Fixed dimensions for every card — consistent sizing, no shrink on small screens
    card: {
        // flex: `0 0 ${DETAIL_CARD_WIDTH}px`,
        width: DETAIL_CARD_WIDTH,
        height: DETAIL_CARD_HEIGHT,
        boxSizing: "border-box",
        background: COLORS.cardBg,
        border: `1px solid ${iconCircleColor}`,
        borderRadius: 10,
        padding: "12px 14px",
        overflow: "hidden",
    },

    header: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        height: "100%",
        minWidth: 0,
    },

    iconWrapper: {
        width: 42,
        height: 42,
        minWidth: 42,
        flexShrink: 0,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: iconCircleColor,
        border: `1px solid ${iconCircleColor}`,
        boxShadow: "0 4px 10px rgba(55, 138, 221, 0.18)",
    },

    content: {
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
    },

    label: {
        fontSize: 10,
        color: COLORS.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        marginBottom: 4,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },

    value: (colorKey) => {
        const map = {
            green: COLORS.green,
            amber: COLORS.amber,
            red: COLORS.red,
            blue: COLORS.blue,
        };

        return {
            fontSize: 13,
            fontWeight: 600,
            color: colorKey ? map[colorKey] || colorKey : COLORS.textPrimary,
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        };
    },

    sub: {
        fontSize: 11,
        color: COLORS.textMuted,
        marginTop: 3,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
};

// Products table — horizontally scrollable on mobile
export const productsTable = {
    wrapper: {
        border: `1px solid ${COLORS.borderLight}`,
        borderRadius: 10,
        overflow: "hidden",
        marginBottom: 16,
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        minWidth: 480,
        border: `1px solid ${iconCircleColor}`,
    },
    th: {
        fontSize: 10,
        fontWeight: 700,
        color: COLORS.textMuted,
        padding: "9px 12px",
        textAlign: "center",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        borderBottom: `1px solid ${COLORS.borderLight}`,
        background: COLORS.surfaceAlt,
        whiteSpace: "nowrap",
    },
    td: {
        fontSize: 12,
        color: COLORS.textSecondary,
        padding: "9px 12px",
        whiteSpace: "nowrap",
        textAlign: "center",
    },
    tdName: {
        fontSize: 12,
        color: COLORS.textPrimary,
        fontWeight: 500,
        padding: "9px 12px",
    },
    tdRevenue: {
        fontSize: 12,
        color: COLORS.textPrimary,
        fontWeight: 700,
        padding: "9px 12px",
        whiteSpace: "nowrap",
    },
};

// Insights
export const insights = {
    grid: {
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
    },
    card: {
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        backgroundColor: closedBgColor,
        border: `1px solid ${COLORS.borderLight}`,
        borderRadius: 10,
        padding: "11px 13px",
        flex: "1 1 220px",
        minWidth: 200,
    },
    dot: (color) => ({
        width: 7,
        height: 7,
        borderRadius: "50%",
        backgroundColor: insightsDotColor,
        flexShrink: 0,
        marginTop: 5,
    }),
    text: {
        fontSize: 12,
        color: COLORS.textSecondary,
        lineHeight: 1.65,
    },
};

// Empty state
export const emptyState = {
    wrapper: {
        textAlign: "center",
        padding: "13px 0",
        color: COLORS.textMuted,
        fontSize: 14,
    },
    icon: { fontSize: 34, marginBottom: 10 },
    text: { fontSize: 14, color: COLORS.textMuted, fontWeight: 500 },
};

// ── Individual Challan Card ───────────────────────────────────────────────────
export const challanCard = {
    wrapper: (open) => ({
        background: COLORS.cardBg,
        border: `1.5px solid ${open ? challanOpenBorderColor : COLORS.border}`,
        borderRadius: 14,
        boxShadow: open
            ? "0 6px 28px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.04)"
            : "0 1px 4px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.25s, border-color 0.25s",
    }),

    // IMPROVED: header is now a responsive flex container.
    // On mobile it stacks vertically; on ≥600px it stays horizontal.
    // We handle this via wrapping + min-widths rather than media queries
    // (pure inline style approach as required).
    header: (open) => ({
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap", // allows wrapping on narrow screens
        gap: 10,
        cursor: "pointer",
        borderBottom: open ? `1.5px solid ${COLORS.borderLight}` : "none",
        background: open ? COLORS.surfaceAlt : COLORS.cardBg,
        borderRadius: open ? "14px 14px 0 0" : 14,
        transition: "background 0.2s",
    }),

    headerLeft: {
        display: "flex",
        flexDirection: "column",
        gap: 5,
        minWidth: 180,
        flex: "1 1 180px",
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
    },
    title: {
        fontSize: 17,
        fontWeight: 700,
        color: COLORS.textPrimary,
        letterSpacing: "-0.01em",
    },
    meta: {
        fontSize: 11.5,
        color: COLORS.textMutedForSubTite,
        lineHeight: 1.5,
        // FIX: allow meta text to wrap cleanly on mobile
        wordBreak: "break-word",
    },

    // IMPROVED: tag group — each row is a flex row where both tags share equal width.
    // By setting `flex: 1` on the tag wrapper span, both pills grow equally within the row.
    tagGroup: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch", // stretch so children (tagRow) fill available width
        gap: 6,
    },
    // IMPROVED: tagRow is a flex row with equal-width children.
    // Each tag spans exactly half the container width via flex: 1.
    tagRow: {
        display: "flex",
        alignItems: "stretch",
        gap: 6,
    },

    // IMPROVED: center section that holds the tag group
    // flex: "0 1 auto" prevents it from growing aggressively;
    // it sits naturally between left and right panels.
    headerCenter: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 8px",
        flex: "0 1 340px", // constrained max width for the tag block
        minWidth: 260,
        // On narrow screens this section wraps below the left panel
    },

    // IMPROVED: right panel keeps stats + action icons together.
    // Uses flex-wrap: nowrap so icons don't orphan.
    headerRight: {
        display: "flex",
        alignItems: "center",
        alignSelf: "stretch",
        paddingLeft: 8,
        gap: 0,
        flexShrink: 0,
        flexWrap: "nowrap",
    },

    headerRightIcons: {
        display: "flex",
        alignItems: "center",
        alignSelf: "stretch",
        paddingLeft: 3,
        gap: 3,
        flexShrink: 0,
        flexWrap: "nowrap",
    },

    // IMPROVED: statBlock has a defined min/max width and uses padding
    // rather than margin to control spacing, improving column alignment.
    statBlock: () => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minWidth: 80,
        padding: "0 14px",
        borderRight: `1px solid ${COLORS.borderLight}`,
    }),
    statValue: (color) => ({
        fontSize: 18,
        fontWeight: 800,
        color: color || COLORS.textPrimary,
        letterSpacing: "-0.03em",
        lineHeight: 1.1,
    }),
    statLabel: {
        fontSize: 10,
        color: COLORS.textMuted,
        marginTop: 4,
        fontWeight: 600,
        whiteSpace: "nowrap",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },

    chevron: (open) => ({
        width: 28,
        height: 28,
        borderRadius: "50%",
        border: `1.5px solid ${COLORS.border}`,
        background: open ? COLORS.accentLight : COLORS.surfaceAlt,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transform: open ? "rotate(180deg) scale(1.05)" : "rotate(0deg) scale(1)",
        transition: "all 0.35s ease-in-out",
        boxShadow: open
            ? `0 4px 12px rgba(55, 138, 221, 0.18)`
            : "0 2px 6px rgba(0,0,0,0.04)",
        cursor: "pointer",
        flexShrink: 0,
        marginLeft: 10,
    }),

    iconBtnVariant: (borderColor, bgColor) => ({
        width: 28,
        height: 28,
        borderRadius: "50%",
        border: `1.5px solid ${borderColor}`,
        background: bgColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginLeft: 6,
        cursor: "pointer",
        userSelect: "none",
        transition: "opacity 0.18s, transform 0.15s",
    }),

    tagIcon: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        lineHeight: 1,
    },

    body: {
        padding: "18px 16px 18px",
        maxHeight: open ? "3000px" : "0px",
        opacity: open ? 1 : 0,
        transform: open ? "scaleY(1)" : "scaleY(0.96)",
        overflow: "hidden",
        transition: "all 0.2s ease",
        padding: open ? "16px" : "0px 16px"
    },
};